// Plan summary card for /usage.
//
// Renders: current plan name + category, subscription state pill,
// start/end date, days remaining (with escalating colour for trial),
// and a Shop CTA when the org is on trial or expired.
//
// All data comes from useSubscriptionStatus — no extra fetch.

import { Button, Card, Skeleton, Statistic, Tag, Typography } from "antd";
import { Crown } from "lucide-react";
import { Link } from "react-router-dom";

const { Title, Text } = Typography;

function fmtDate(s) {
  if (!s) return "-";
  try {
    return new Date(s).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return s;
  }
}

function stateTag(status) {
  if (!status) return null;
  if (status.is_trial && status.trial_expired_at) {
    return <Tag color="red">Trial expired</Tag>;
  }
  if (status.is_trial) {
    return <Tag color="orange">Free trial</Tag>;
  }
  // Plan-catalogue categories. Premium tier (now "Pro"; old "Advanced") is
  // purple, basic tier (now "Track"; old "Essential") is blue. Old names kept
  // as aliases so pre-migration data still renders. The Tag text echoes the
  // actual category so a renamed plan shows its real name.
  const cat = status.subscription_category;
  if (cat === "Pro" || cat === "Advanced") return <Tag color="purple">{cat}</Tag>;
  if (cat === "Track" || cat === "Essential") return <Tag color="blue">{cat}</Tag>;
  return <Tag color="green">Active</Tag>;
}

export default function PlanSummaryCard({ status, isLoading }) {
  if (isLoading || !status) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 3 }} />
      </Card>
    );
  }

  const isTrial = !!status.is_trial;
  const isExpired = !!(status.is_trial && status.trial_expired_at);
  const daysLeft =
    typeof status.days_left === "number"
      ? status.days_left
      : Math.max(0, status.days_remaining || 0);
  const endDate = isTrial ? status.trial_end_date : status.subscription_end_date;
  const startDate = isTrial ? status.trial_start_date : status.subscription_start_date;

  let daysValueColour;
  if (isTrial) {
    if (daysLeft <= 2) daysValueColour = "#f5222d";
    else if (daysLeft <= 7) daysValueColour = "#faad14";
    else daysValueColour = "#1d4ed8";
  } else {
    daysValueColour = "#1d4ed8";
  }

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Title level={3} style={{ margin: 0 }}>
              {status.subscription_plan || (isTrial ? "Free Trial" : "-")}
            </Title>
            {stateTag(status)}
          </div>
          <Text type="secondary">
            {startDate ? `${fmtDate(startDate)} → ${fmtDate(endDate)}` : "-"}
          </Text>
        </div>
        <div className="flex items-center gap-6">
          <Statistic
            title={isExpired ? "Trial ended" : "Days remaining"}
            value={isExpired ? "-" : daysLeft}
            valueStyle={{ color: daysValueColour, fontSize: 28 }}
          />
          {(isTrial || isExpired) && (
            <Link to="/shop">
              <Button
                type="primary"
                icon={<Crown size={16} strokeWidth={2.25} />}
                size="large"
              >
                {isExpired ? "Buy a plan" : "Upgrade now"}
              </Button>
            </Link>
          )}
        </div>
      </div>
    </Card>
  );
}
